import { getBulkActionButtonConfig } from "@components/common/faro-table/bulk-actions/bulk-actions-config";
import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { BulkSuccessDialog } from "@components/common/faro-table/bulk-dialog/bulk-success-dialog";
import { FaroTextMenuButton } from "@components/common/faro-text-menu-button";
import { useSceneSeatPermission } from "@components/table/members/assign-seats/use-scene-seat-permission";
import { getSeatBulkChangeLabel, getSeatBulkSkipReason, hasSeatShortage, SEAT_BULK_CHANGES, useSeatBulkDialogContent } from "@components/table/members/bulk-actions/bulk-seats-helper";
import { SeatShortageAlert } from "@components/table/members/bulk-actions/seat-shortage-alert";
import { useCompanyId } from "@context-providers/company-id-provider";
import { MenuItem, SeatIcon, Text } from "@flat-ui";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { useMemberSeatsLookup } from "@hooks/use-member-seats-lookup";
import { setMemberPermissionsInCompany } from "@store/members/members-slice-thunk";
import { sceneSeatsAvailableSelector } from "@store/scene-seats/scene-seats-selector";
import { activeSeatUsageSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { bulkActionNameSelector, selectedEntitiesSelector, shouldShowSuccessDialogSelector } from "@store/table/table-selector";
import { setBulkActionName, updateMany } from "@store/table/table-slice";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useEffect, useState } from "react";

/**
 * Button that gives every selected member a seat, or takes their SCENE seat away, after confirming
 * it in a dialog. A Sphere XG seat can only be given, never taken away again.
 *
 * The members that the change does not apply to are still listed in the dialog, so that the user
 * sees the whole selection they made, but they are left out of the requests.
 *
 * Both seats are permissions of the member: `activeSeat` for Sphere XG and `sceneSeat` for SCENE.
 */
export function BulkSeats() {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    setSceneSeatPermission,
    refreshSeatUsage
  } = useSceneSeatPermission();
  const {
    isActiveSeatPlan,
    hasSceneSeats
  } = useMemberSeatsLookup();
  const {
    canManageXgSeats,
    canManageSceneLicenses
  } = useHasUserValidRoleCompanyLevel();
  const [shouldShowDialog, setShouldShowDialog] = useState(false);
  const [change, setChange] = useState(SEAT_BULK_CHANGES[0]);
  const bulkActionName = useAppSelector(bulkActionNameSelector);
  const shouldShowSuccessDialog = useAppSelector(shouldShowSuccessDialogSelector);
  const selectedEntities = useAppSelector(selectedEntitiesSelector("members"));
  const sceneSeatsAvailable = useAppSelector(sceneSeatsAvailableSelector);
  const activeSeatUsage = useAppSelector(activeSeatUsageSelector);
  const mode = useBulkActionsMode();
  const {
    bulkDialogContent,
    successDialogContent
  } = useSeatBulkDialogContent(change);

  // The selection has to fit the free seats before it can be confirmed, so that the workspace
  // never hands out more seats than its plan allows
  const seatShortage = change.isAssigning ? {
    numberOfChangingMembers: getChangingMembers(change).length,
    seatsAvailable: getSeatsAvailable(change)
  } : undefined;
  const isShortOfSeats = !!seatShortage && hasSeatShortage(seatShortage);

  // Closes the bulk dialog when the bulk action is successful to show the success dialog
  useEffect(() => {
    if (shouldShowSuccessDialog) {
      setShouldShowDialog(false);
    }
  }, [shouldShowSuccessDialog]);
  const config = getBulkActionButtonConfig(mode, "Change seats");

  // Only the changes the user may make and that apply to anybody in the selection are offered
  const offeredChanges = SEAT_BULK_CHANGES.filter(seatChange => canMakeChange(seatChange) && getChangingMembers(seatChange).length > 0);
  return <>
      <FaroTextMenuButton startIcon={<SeatIcon sx={{
      width: "18px",
      height: "18px"
    }} />} buttonTitle={config.shouldShowText ? <Text variant="buttonM">Seats</Text> : undefined} tooltipText={config.tooltipText} buttonSx={() => config.sx} menuPosition={{
      anchorOrigin: {
        vertical: "top",
        horizontal: "left"
      },
      transformOrigin: {
        vertical: "bottom",
        horizontal: "left"
      }
    }} rippleColor={config.rippleColor} shouldShowEndIcon={false} isDisabled={offeredChanges.length === 0}>
        {offeredChanges.map(seatChange_0 => {
        const label = getSeatBulkChangeLabel(seatChange_0);
        return <MenuItem key={label} value={label} onClick={() => openDialog(seatChange_0)}>
              {label}
            </MenuItem>;
      })}
      </FaroTextMenuButton>

      {/* Checking for bulkActionName to make sure the dialog of another bulk action is not opened by mistake */}
      {bulkActionName === "seats" && shouldShowSuccessDialog && <BulkSuccessDialog dialogContent={successDialogContent} />}

      {bulkActionName === "seats" && shouldShowDialog && <FaroBulkDialog dialogContent={bulkDialogContent} onClose={() => {
      setShouldShowDialog(false);
      // Some members may have been changed even when others failed
      refreshSeatUsage();
    }} trackingEvent={change.seat === "xgSeat" ? BulkActionEvents.bulkAssignXgSeat : BulkActionEvents.bulkChangeSceneAccess} shouldDisableConfirmButton={isShortOfSeats} bulkActionCallback={changeSeatOfMember} onSuccessfulComplete={refreshSeatUsage} selectedEntities={selectedEntities} uniqueIdKey="identity" tableDataType="members">
          {seatShortage && <SeatShortageAlert {...seatShortage} />}
        </FaroBulkDialog>}
    </>;

  /** Whether the plan includes the seat and the user may make the change */
  function canMakeChange({
    seat
  }) {
    return seat === "xgSeat" ? isActiveSeatPlan && canManageXgSeats : hasSceneSeats && canManageSceneLicenses;
  }

  /** Returns the selected members that the change applies to */
  function getChangingMembers(seatChange_1) {
    return selectedEntities.filter(member => getSeatBulkSkipReason(member, seatChange_1) === undefined);
  }

  /** Returns how many of the seats are still free to be given to a member */
  function getSeatsAvailable({
    seat: seat_0
  }) {
    if (seat_0 === "sceneSeat") {
      return sceneSeatsAvailable;
    }
    return activeSeatUsage ? Math.max(activeSeatUsage.max - activeSeatUsage.used, 0) : 0;
  }

  /**
   * Marks the members that the change does not apply to, so that the bulk dialog lists them but
   * leaves them out of the requests, and opens the dialog to confirm the change.
   */
  function openDialog(seatChange_2) {
    dispatch(setBulkActionName("seats"));
    const skippedChanges = [];
    for (const member_0 of selectedEntities) {
      const skipReason = getSeatBulkSkipReason(member_0, seatChange_2);
      if (skipReason) {
        skippedChanges.push({
          id: member_0.identity,
          changes: {
            status: "not-allowed",
            message: skipReason
          }
        });
      }
    }
    if (skippedChanges.length) {
      dispatch(updateMany(skippedChanges));
    }
    setChange(seatChange_2);
    setShouldShowDialog(true);
  }

  /** Gives the member the seat or takes it away, depending on the selected change */
  async function changeSeatOfMember(member_1) {
    if (change.seat === "sceneSeat") {
      await setSceneSeatPermission(member_1, change.isAssigning);
      return;
    }
    await dispatch(setMemberPermissionsInCompany({
      companyId,
      userIdentity: member_1.identity,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Used in backend
      permissions: [{
        name: "activeSeat",
        allowed: true
      }]
    })).unwrap();
  }
}